import * as React from "react";
import {required, Text} from "components/form";
import Summary from "components/form/Sections/Summary";
import ExpandableFormSection from "components/form/Sections/ExpandableFormSection";
import FormPaperLayout from "components/FormPaperLayout/FormPaperLayout";
import OptionalFormBaseComponent, {OptionalFormBaseComponentState} from "components/form/FormBaseComponent";
import repository from "client/repository";
import {InternalRedirect} from "components/Navigation/InternalRedirect";
import {CreateDynamicWorkerServiceResource} from "client/resources/createDynamicWorkerServiceResource";
import {cloneDeep} from "lodash";
import {RouteComponentProps} from "react-router-dom";
import {dynamicWorkerServicesRouteLinks} from "areas/dynamicWorkerServices/dynamicWorkerServicesRouteLinks";
type Props = RouteComponentProps<{ id: string }>;

interface State extends OptionalFormBaseComponentState<CreateDynamicWorkerServiceResource> {
    redirectTo?: string;
}

export class CreateDynamicWorkerService extends OptionalFormBaseComponent<Props, State, CreateDynamicWorkerServiceResource> {
    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    async componentDidMount() {
        const model = {
                Name: "",
                InstanceId: "",
                LeasingApiWebAppResourceId: "",
                Url: "",
                SeqDashboardName: "",
                AppRegistrationAudience: "",
            };
        
        this.setState({
            model,
            cleanModel: cloneDeep(model),
        });
    }

    render() {

        const state = this.state;

        if (state.redirectTo) {
            return <InternalRedirect to={state.redirectTo}/>;
        }
        
        const actionName = "Connect to Existing Dynamic Worker Service";
        
        
        return <FormPaperLayout
            title={actionName}
            busy={state.busy}
            errors={state.errors}
            model={state.model}
            cleanModel={state.cleanModel}
            saveButtonLabel={actionName}
            saveText="Dynamic Worker Service connected"
            saveButtonBusyLabel={"Saving"}
            expandAllOnMount={true}
            onSaveClick={this.handleSaveClick}
        >
            {state.model && this.getBody()}
        </FormPaperLayout>;
    }

    getBody() {
        const model = this.state.model;

        return <div>
            <ExpandableFormSection
                errorKey="Name"
                title="Name"
                summary={model.Name ? Summary.summary(model.Name) : Summary.placeholder("No name")}
                help={<span>Enter the display name for this service.</span>}>
                <Text label="Name"
                      value={model.Name}
                      onChange={Name => this.setModelState({Name})}
                      validate={required("Please enter the name to use for this Dynamic Worker Service")}
                      error={this.getFieldError("Name")}
                      autoFocus={true}
                />
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="InstanceId"
                title="Instance Id"
                summary={model.InstanceId ? Summary.summary(model.InstanceId) : Summary.placeholder("No Instance ID")}
                help={<span>Enter the Instance Id for this DW service (see 'leasingapi-{`{`}Instance Id{`}`}' in the service's URL) </span>}>
                <Text label="InstanceId"
                      value={model.InstanceId}
                      onChange={InstanceId => this.setModelState({InstanceId})}
                      validate={required("Please enter the Instance ID to use for this Dynamic Worker Service")}
                      error={this.getFieldError("InstanceId")}
                />
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="LeasingApiWebAppResourceId"
                title="Leasing Api Web App Resource Id"
                summary={model.LeasingApiWebAppResourceId ? Summary.summary(model.LeasingApiWebAppResourceId) : Summary.placeholder("No Leasing Api Web App Resource Id")}
                help={<span>Enter the Resource ID for this service's LeasingApi Web App - see in 'JSON View' in Azure Portal for this web app</span>}>
                <Text label="LeasingApiWebAppResourceId"
                      value={model.LeasingApiWebAppResourceId}
                      onChange={LeasingApiWebAppResourceId => this.setModelState({LeasingApiWebAppResourceId: LeasingApiWebAppResourceId})}
                      validate={required("Please enter the resource id to use for this Dynamic Worker Service's leasingApi web app")}
                      error={this.getFieldError("LeasingApiWebAppResourceId")}
                />
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="Url"
                title="Url"
                summary={model.Url ? Summary.summary(model.Url) : Summary.placeholder("No Url")}
                help={<span>Enter the url for this service.</span>}>
                <Text label="Url"
                      value={model.Url}
                      onChange={Url => this.setModelState({Url})}
                      validate={required("Please enter the Url for this service")}
                      error={this.getFieldError("Url")}
                />
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="AppRegistrationAudience"
                title="App Registration Audience"
                summary={model.AppRegistrationAudience ? Summary.summary(model.AppRegistrationAudience) : Summary.placeholder("No app registration audience")}
                help={<span>Enter the Application ID URI for this service's App Registration - see the Overview page in Azure Portal for this App Registration</span>}>
                <Text label="AppRegistrationAudience"
                      value={model.AppRegistrationAudience}
                      onChange={AppRegistrationAudience => this.setModelState({AppRegistrationAudience})}
                      validate={required("Please enter the App Registration Audience for this service")}
                      error={this.getFieldError("AppRegistrationAudience")}
                />
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="SeqDashboardName"
                title="Seq Dashboard Name"
                summary={model.SeqDashboardName ? Summary.summary(model.SeqDashboardName) : Summary.placeholder("No dashboard name")}
                help={<span>Enter the Seq Dashboard Name for this service.</span>}>
                <Text label="SeqDashboardName"
                      value={model.SeqDashboardName}
                      onChange={SeqDashboardName => this.setModelState({SeqDashboardName})}
                      validate={required("Please enter the Seq Dashboard Name for this service")}
                      error={this.getFieldError("SeqDashboardName")}
                />
            </ExpandableFormSection>
        </div>;
    }

    private handleSaveClick = () => {
        return this.doBusyTask(async () => {
            await repository.DynamicWorkerServices.create(this.state.model as CreateDynamicWorkerServiceResource);
            this.setState({redirectTo: dynamicWorkerServicesRouteLinks.root});
        });
    };
}
